import { storeToRefs } from 'pinia';
import { NavigationGuard, RouteLocationNormalized } from 'vue-router';

import { useErrorPage } from '@/composables/useErrorPage';
import { ROUTE_NAMES, SPLIT_NAMES, URLS } from '@/constants';
import { RouteMetaPermission } from '@/models/permission';
import { useAppConfigStore } from '@/piniaStores/appConfig/appConfig.store';
import { useAuthStore } from '@/piniaStores/auth/auth.store';
import { useCompanyStore } from '@/piniaStores/company/company.store';
import { useShopStore } from '@/piniaStores/shop/shop.store';
import { useUserStore } from '@/piniaStores/user/user.store';
import { identifySegmentUser, loadSegmentAnalytics, reportSegmentExperimentAllocations } from '@/segment';
import { getAuth0Client, initAuth0 } from '@/services/auth';
import { isHttpError } from '@/utils/errors';
import validatePermissions from '@/utils/validatePermissions';

import { isVueRoute } from './utils';

export const appConfigGuard: NavigationGuard = async () => {
  const appConfigStore = useAppConfigStore();

  if (!appConfigStore.hasLoaded) {
    try {
      await appConfigStore.fetchAppConfig();
    } catch (error) {
      // This ensures that the loading indicator in App.vue is removed in case the app config fails to load or responds with an error
      if (!isHttpError(error)) {
        throw error;
      }
    }
  }
};

export const authenticationGuard: NavigationGuard = async () => {
  const appConfigStore = useAppConfigStore();

  if (!appConfigStore.unifiedConfig?.enabled) {
    // At the time of writing, the unified API is not enabled in the sandbox environment.
    return;
  }

  if (!getAuth0Client()) {
    await initAuth0({
      domain: appConfigStore.unifiedConfig.domain,
      clientId: appConfigStore.unifiedConfig.clientId,
      audience: appConfigStore.unifiedConfig.audience,
    });
  }

  const authStore = useAuthStore();
  const token = await authStore.login();

  // authStore.login() redirects to the login page before it returns false, therefore it is safe to return false here to stop navigation while the user is logging in
  if (!token) {
    return false;
  }
};

export const authenticationCallbackGuard: NavigationGuard = async () => {
  const appConfigStore = useAppConfigStore();

  if (!appConfigStore.unifiedConfig?.enabled) {
    // At the time of writing, the unified API is not enabled in the sandbox environment.
    return;
  }

  let auth0 = getAuth0Client();

  if (!auth0) {
    auth0 = await initAuth0({
      domain: appConfigStore.unifiedConfig.domain,
      clientId: appConfigStore.unifiedConfig.clientId,
      audience: appConfigStore.unifiedConfig.audience,
    });
  }

  let result;

  try {
    result = await auth0?.handleRedirectCallback();
  } catch (e) {
    window.location.href = `${window.location.origin}/accounts/logout/`;
  }

  if (typeof result?.appState?.target === 'string') {
    return result.appState.target;
  }

  return { name: ROUTE_NAMES.DASHBOARD };
};

/**
 * Ensures a login redirect from Auth0 is handled when redirected back to the app with the authorization code.
 */
export const authenticationRedirectGuard: NavigationGuard = (to) => {
  if (to.query.code && to.query.state && to.name === ROUTE_NAMES.OAUTH2_CALLBACK) {
    return true;
  } else if (to.query.code && to.query.state) {
    // this handles if a redirect from Auth0 is somehow not caught by the Oauth2Callback route
    return { name: ROUTE_NAMES.OAUTH2_CALLBACK };
  }
};

type FetchCommonDataOptions = {
  forceLoad: boolean;
};

/**
 * Fetches data that is commonly needed by most pages (such as permissions, user data, and company data).
 * Also, it loads the Segment Analytics library and identifies the user with Segment.
 * Will not refetch data on subsequent calls when used as a router navigation guard.
 * Can optionally pass an option to force refetching data.
 * The loadSegmentAnalytics function relies on fetchCommonData to call fetchUser and fetchCompany
 *
 * **Testing Notice:**
 *  - The `mockCommonData` function in `tests/utils/mockCommonData.ts` provides initial values for all the data
 *    that `fetchCommonData` fetches in order to prevent unnecessary unmocked network requests. Be sure
 *    to keep `mockCommonData` up-to-date with the data that `fetchCommonData` fetches or else MSW may
 *    throw errors (correctly) for unmocked networks requests.
 */
export async function fetchCommonData(toOrOptions?: RouteLocationNormalized | FetchCommonDataOptions): Promise<void> {
  const userStore = useUserStore();
  const { userActiveCompany, getIsSplitEnabled } = storeToRefs(userStore);
  const companyStore = useCompanyStore();
  const { activeCompany, isAppLoaded } = storeToRefs(companyStore);

  const options: FetchCommonDataOptions =
    typeof toOrOptions === 'object' && 'forceLoad' in toOrOptions ? toOrOptions : { forceLoad: false };

  if (isAppLoaded.value && !options.forceLoad) {
    return;
  }

  try {
    await Promise.all([userStore.fetchUser(), userStore.fetchSplits(Object.values(SPLIT_NAMES).join(','))]);

    if (userActiveCompany.value) {
      await companyStore.fetchCompany({ companyId: userActiveCompany.value.id });
    }

    loadSegmentAnalytics();
    identifySegmentUser();

    await reportSegmentExperimentAllocations();

    const pendingRequests: Promise<unknown>[] = [];

    if (activeCompany.value) {
      pendingRequests.push(userStore.fetchS2SPermissions());
      pendingRequests.push(companyStore.fetchBadgeCounts());

      if (activeCompany.value.buyer) {
        const shopStore = useShopStore();

        pendingRequests.push(shopStore.fetchActiveCampaign());
        pendingRequests.push(shopStore.fetchFavoritesItems(activeCompany.value.id));
      }
    }

    await Promise.all(pendingRequests);
  } catch (error) {
    if (!isHttpError(error)) {
      throw error;
    }
  } finally {
    isAppLoaded.value = true;
  }
}

/**
 * Initiates a full page load if navigating to or from a page that contains HTML from outside this Vue app instance.
 * If going to a non-Vue page, a reload is needed to fetch the HTML for that page.
 * If coming from a non-Vue page, a reload is needed to fetch the `vue/index.html` file from leaflink/leaflink.
 * If a path is changed with a different query string or hash, the page is not reloaded to avoid redirects. On cases of legacy pages that use query strings or hashes to load content (ie. `/c/company-slug/settings/#tab-profile`), which doesn't change the base path, the page shouldn't be reloaded.
 */
export const serverRenderedRouteGuard: NavigationGuard = async (to, from) => {
  // When Vue router is initialized, the first route is always '/' and `to.redirectedFrom` will always be undefined.
  const isInitialLoad = from.path === '/' && to.redirectedFrom === undefined;
  const isComingFromOrGoingToNonVuePage = !isVueRoute(to) || !isVueRoute(from);
  const isSamePath = to.path === from.path;

  const { getIsSplitEnabled } = storeToRefs(useUserStore());

  if (
    !getIsSplitEnabled.value(SPLIT_NAMES.SSR_IFRAME) &&
    !isInitialLoad &&
    isComingFromOrGoingToNonVuePage &&
    !isSamePath
  ) {
    window.location.href = `${window.location.origin}${to.fullPath}`;
    return false;
  }

  if (!isVueRoute(to)) {
    // @ts-expect-error "requires 3 arguments"; ignoring this error because none of the arguments are used
    await authenticationGuard();
    await fetchCommonData(); // needed for the AppHeader and AppSidebar on Django pages
  }

  // Some Django pages can change common data (ie. user, company, and profile data). To avoid stale data,
  // we'll want to refetch common data when navigating from a Django page to a Vue page.
  const acceptedRefetchUris = ['/settings/', '/user/'];
  const refetchCommonData = acceptedRefetchUris.some((url) => from.fullPath.includes(url));

  if (!isVueRoute(from) && refetchCommonData && !isInitialLoad && getIsSplitEnabled.value(SPLIT_NAMES.SSR_IFRAME)) {
    await fetchCommonData({ forceLoad: true });
  }
};

/**
 * Validates route-defined permissions and redirects to the specificed route if the user does not have the required permissions.
 *
 * Example:
 *
 * ```ts
 * {
 *   name: 'settings',
 *   path: '/settings',
 *   meta: {
 *     permissions: [
 *       { rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin)] },
 *     ],
 *   },
 * },
 * {
 *   name: 'new-feature',
 *   path: '/new-feature',
 *    meta: {
 *      permissions: ['split_io_flag_for_new_feature'],
 *    },
 * },
 * ```
 *
 * @see {@link validatePermissions} for more information on the rules
 */
export const metaPermissionsGuard: NavigationGuard = (to) => {
  const navPermissions = to.matched
    .filter((route) => route.meta.permissions)
    .map((route) => route.meta.permissions as RouteMetaPermission[])
    .flat();

  if (!navPermissions.length) {
    return;
  }

  const { activeCompany, integrationType } = storeToRefs(useCompanyStore());
  const { getIsSplitEnabled, user, userActiveCompany, userActiveCompanyPermissions } = storeToRefs(useUserStore());

  const failedPermissions = navPermissions.filter((permission) => {
    const { isValid } = validatePermissions({
      rules: permission.rules,
      activeCompany: activeCompany.value,
      integrationType: integrationType.value,
      userActiveCompanyPermissions: userActiveCompanyPermissions.value,
      user: user.value,
      userActiveCompany: userActiveCompany.value,
      isSplitEnabled: getIsSplitEnabled.value,
    });

    return !isValid;
  });

  if (failedPermissions.length) {
    const permissionWithRedirect = failedPermissions.find((permission) => permission.redirect);

    if (permissionWithRedirect?.redirect) {
      if (permissionWithRedirect.redirect instanceof Function) {
        return permissionWithRedirect.redirect(to);
      }

      return permissionWithRedirect.redirect;
    }

    useErrorPage().settings = { errorCode: 403 };
  }
};

/**
 *
 * Used to check if the active company is delinquent on their invoices to LeafLink.
 * If the user has the billing permission they get redirected to that page.
 * If the user does not have the permissions they will get redirected to a different page describing the issue.
 *
 * @param to value from `beforeEnter`
 */
export const delinquencyGuard: NavigationGuard = (to) => {
  const companyStore = useCompanyStore();
  const { activeCompany } = storeToRefs(companyStore);

  const { getIsSplitEnabled, userActiveCompanyPermissions } = storeToRefs(useUserStore());
  const isDeliquentLockdownFlagOn = getIsSplitEnabled.value(SPLIT_NAMES.DELINQUENT_LOCKDOWN);
  const canManageBilling = userActiveCompanyPermissions.value?.canManageBilling;

  if (
    companyStore.isDelinquent(isDeliquentLockdownFlagOn) &&
    !([ROUTE_NAMES.BILLING, ROUTE_NAMES.BILLING_DUE] as string[]).includes(String(to.name))
  ) {
    const companySlug = activeCompany.value?.slug || '';

    if (isVueRoute(to) || getIsSplitEnabled.value(SPLIT_NAMES.SSR_IFRAME)) {
      return {
        name: canManageBilling ? ROUTE_NAMES.BILLING : ROUTE_NAMES.BILLING_DUE,
        params: { company: companySlug },
      };
    } else {
      const path = canManageBilling ? URLS.SETTINGS.BILLING.BASE : URLS.SETTINGS.BILLING_DUE;

      window.location.href = `${window.location.origin}/c/${companySlug}/settings/${path}/`;

      return false;
    }
  }
};

export const licenseGuard: NavigationGuard = (to) => {
  const companyStore = useCompanyStore();
  const { activeCompany } = storeToRefs(companyStore);
  const userStore = useUserStore();
  const { getIsSplitEnabled, userActiveCompanyPermissions } = storeToRefs(userStore);

  if (
    companyStore.isLicenseLocked(userStore.getIsSplitEnabled(SPLIT_NAMES.PAUSE_INACTIVE_LICENSE_COMPANIES)) &&
    !([ROUTE_NAMES.LICENSES, ROUTE_NAMES.LICENSES_DUE] as string[]).includes(String(to.name))
  ) {
    const companySlug = activeCompany.value?.slug || '';
    const staffIsAdmin = userActiveCompanyPermissions.value?.staffIsAdmin;

    if (isVueRoute(to) || getIsSplitEnabled.value(SPLIT_NAMES.SSR_IFRAME)) {
      return {
        name: staffIsAdmin ? ROUTE_NAMES.LICENSES : ROUTE_NAMES.LICENSES_DUE,
        params: { company: companySlug },
      };
    }
  }
};
